<script setup>
const props = defineProps({
  rowsCount: {
    type: Number,
    default: 1,
  },
  validationSchema: {
    type: Object,
    default: undefined,
  },
  submitCallback: {
    type: Function,
    default: () => {},
  },
  gapSize: {
    type: String,
    default: undefined,
    validator(gapSize) {
      return ['small'].includes(gapSize);
    },
  },
});
const emit = defineEmits(['submit-reset']);

const { handleSubmit, isSubmitting, meta, handleReset, values } = useForm({
  validationSchema: props.validationSchema,
});

const isFormSubmitted = ref(false);

watch(isFormSubmitted, (isFormSubmitted) => {
  if (!isFormSubmitted) emit('submit-reset');
});

const submit = handleSubmit(async (values, actions) => {
  try {
    await props.submitCallback(values, actions);
    isFormSubmitted.value = true;
  } catch (error) {
    // ignore
  }
});

watch(values, () => {
  isFormSubmitted.value = false;
});

const isFormSubmitting = computed(() => meta.value.valid && isSubmitting.value);

const formState = computed(() =>
  getFormState(isFormSubmitting.value, isFormSubmitted.value),
);

function reset() {
  handleReset();
  isFormSubmitted.value = false;
}
</script>

<template>
  <form
    class="ui-form"
    :class="{ [`ui-form--gap-size--${gapSize}`]: gapSize }"
    novalidate
    @submit.prevent="submit"
    @reset="reset"
  >
    <div class="ui-form__wrapper">
      <div v-for="row in rowsCount" :key="row" class="ui-form__row">
        <slot :name="`row-${row}`" />
      </div>
    </div>

    <slot
      name="footer"
      :is-form-submitting="isFormSubmitting"
      :is-form-submitted="isFormSubmitted"
      :form-state="formState"
    />
  </form>
</template>

<style scoped lang="scss">
.ui-form {
  $parent: &;

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: em(38);

    @include media-breakpoint-down(sm) {
      gap: em(22);
    }
  }

  &__row {
    display: grid;
    gap: em(8);

    @include media-breakpoint-up(sm) {
      grid-auto-flow: column;
      grid-auto-columns: 1fr;
    }

    @include media-breakpoint-down(sm) {
      gap: em(22);
    }
  }

  &--gap-size {
    &--small {
      #{$parent} {
        &__wrapper {
          gap: em(18);
        }

        &__row {
          @include media-breakpoint-down(sm) {
            gap: em(18);
          }
        }
      }
    }
  }
}
</style>
